/**
 * 튜토리얼/가이드 HTML 언어 동기화 유틸 (의존성 없음).
 *
 * 모든 walkthrough·user-guide HTML 은 우선 언어를 same-origin localStorage 의
 * `lt_lang` 키에서 읽는다. 앱 언어가 바뀔 때마다 이 값을 갱신해두면,
 * 어느 진입점에서 튜토리얼을 열든 항상 앱 언어로 표시된다.
 *
 * 순환 의존을 피하려고 별도 파일로 분리 (languageStore 등에서 import).
 */

/**
 * 앱 언어 코드 → 튜토리얼 HTML 언어 코드(lt_lang) 매핑.
 * walkthrough·user-guide 지원 코드: en-GB / en-US / ms / zh-CN / zh-TW / vi / ja / ko
 * (앱 코드: en, en-GB, ko, vn, ms, zh-cn, zh-tw)
 */
const TUTORIAL_LANG_MAP: Record<string, string> = {
  en: 'en-GB',
  'en-gb': 'en-GB',
  'en-us': 'en-US',
  ko: 'ko',
  ms: 'ms',
  vn: 'vi',
  vi: 'vi',
  ja: 'ja',
  zh: 'zh-CN',
  'zh-cn': 'zh-CN',
  'zh-hans': 'zh-CN',
  'zh-sg': 'zh-CN',
  'zh-tw': 'zh-TW',
  'zh-hant': 'zh-TW',
  'zh-hk': 'zh-TW',
};

/** 앱 언어 코드를 튜토리얼 HTML 의 lt_lang 코드로 변환 (미지원 시 en-GB). */
export function toTutorialLang(appLang?: string | null): string {
  const key = (appLang || '').toLowerCase();
  return TUTORIAL_LANG_MAP[key] || TUTORIAL_LANG_MAP[key.split('-')[0]] || 'en-GB';
}

/**
 * 튜토리얼 HTML 들이 언어를 읽는 localStorage 키 모음.
 * 파일마다 키가 제각각이라(예: t-sign=lt_lang_tsign, getting-started=lt_lang_onboard),
 * 동기화 시 모든 키에 같은 값을 써 둔다.
 *   - lt_lang          : 대부분의 walkthrough + user-guide
 *   - lt_lang_tsign    : T-Sign
 *   - lt_lang_sg       : start-task-sg, e-filing-sg
 *   - lt_lang_onboard  : getting-started (my/sg)
 *   - lt_lang_ar       : annual-return-my
 *   - lt_lang_rem      : lettaga_reminder_walkthrough
 */
const TUTORIAL_LANG_KEYS = ['lt_lang', 'lt_lang_tsign', 'lt_lang_sg', 'lt_lang_onboard', 'lt_lang_ar', 'lt_lang_rem'];

/**
 * 현재 앱 언어를 same-origin localStorage 에 기록해 walkthrough·user-guide 와 동기화.
 * 튜토리얼 iframe 을 열기 직전, 그리고 앱 언어가 바뀔 때마다 호출한다.
 */
export function syncTutorialLanguage(appLang?: string | null): void {
  const code = toTutorialLang(appLang);
  try {
    for (const key of TUTORIAL_LANG_KEYS) {
      localStorage.setItem(key, code);
    }
  } catch {
    /* localStorage 비활성 환경은 무시 (walkthrough 가 navigator 언어로 폴백) */
  }
}
